import React, { useState } from 'react'
import { Loader2, X } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import ConfirmDialog from '@/features/integrations/components/ConfirmDialog'
import { toast } from '@/components/ui/use-toast'
import { deleteMailbox } from '../services/mailboxesApi'
import type { Mailbox } from '../types'

const GmailIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#4285f4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#34a853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#fbbc05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#ea4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
)

const OutlookIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#0A2767" d="M44 18.5v16.9a2.1 2.1 0 0 1-2.1 2.1H28V22.1L30 20l12.6-6.2A1.9 1.9 0 0 1 44 15.4v3.1Z" />
    <path fill="#0364B8" d="M28 6H9.1A2.1 2.1 0 0 0 7 8.1v8.3l10.5 6.2L28 28V6Z" />
    <path fill="#0078D4" d="M7 16.4v18.5A2.1 2.1 0 0 0 9.1 37H28V22.1L7 16.4Z" />
    <path fill="#50D9FF" d="M28 22.1V37h13.9a2.1 2.1 0 0 0 2.1-2.1V18.5L28 22.1Z" />
    <circle cx="17.5" cy="22.5" r="6.5" fill="#fff" />
    <path fill="#0078D4" d="M17.5 17.5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 8.3a3.3 3.3 0 1 1 0-6.6 3.3 3.3 0 0 1 0 6.6z" />
  </svg>
)

type Props = {
  workspaceId: string
  mailbox: Mailbox
  onChanged: () => void
}

const MailboxCard: React.FC<Props> = ({ workspaceId, mailbox, onChanged }) => {
  const { currentTheme: t } = useTheme()
  const [showConfirm, setShowConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const isMain = mailbox.role === 'main'
  const isHealthy = mailbox.status === 'connected'
  const providerLabel = mailbox.provider === 'gmail' ? 'Gmail' : 'Outlook'

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await deleteMailbox(workspaceId, mailbox.id)
      toast({ variant: 'success', title: 'Mailbox removed', description: `${mailbox.email} is no longer connected.` })
      onChanged()
    } catch (err) {
      toast({
        variant: 'destructive',
        title: "Couldn't remove mailbox",
        description: err instanceof Error ? err.message : 'Try again in a moment.',
      })
    } finally {
      setDeleting(false)
      setShowConfirm(false)
    }
  }

  return (
    <>
      <div
        className="flex items-center gap-3 rounded-xl p-3.5"
        style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: t.hoverBackground, border: `1px solid ${t.border}` }}
        >
          {mailbox.provider === 'gmail' ? <GmailIcon /> : <OutlookIcon />}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold truncate" style={{ color: t.text }} title={mailbox.email}>
              {mailbox.email}
            </span>
            <span
              className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
              style={
                isMain
                  ? { backgroundColor: t.accent, color: '#fff' }
                  : { backgroundColor: t.hoverBackground, color: t.mutedText, border: `1px solid ${t.border}` }
              }
            >
              {isMain ? 'Main' : 'Secondary'}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ backgroundColor: isHealthy ? '#10B981' : t.errorText }}
            />
            <span className="text-[11.5px]" style={{ color: t.mutedText }}>
              {providerLabel} · {isHealthy ? 'Connected' : 'Needs reconnect'}
            </span>
          </div>
        </div>

        {!isMain && (
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            disabled={deleting}
            aria-label={`Remove ${mailbox.email}`}
            className="flex items-center justify-center w-8 h-8 rounded-md transition-colors disabled:opacity-50"
            style={{ color: t.mutedText, border: `1px solid ${t.border}`, backgroundColor: 'transparent' }}
            onMouseEnter={(e) => {
              if (deleting) return
              e.currentTarget.style.backgroundColor = t.errorBackground
              e.currentTarget.style.color = t.errorText
              e.currentTarget.style.borderColor = t.errorText
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = t.mutedText
              e.currentTarget.style.borderColor = t.border
            }}
          >
            {deleting ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
          </button>
        )}
      </div>

      {showConfirm && (
        <ConfirmDialog
          title="Remove mailbox"
          message={`Remove ${mailbox.email} from this workspace? Zeph will stop using it to send email immediately.`}
          confirmLabel={deleting ? 'Removing…' : 'Yes, remove'}
          loading={deleting}
          onConfirm={handleDelete}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </>
  )
}

export default React.memo(MailboxCard)
